import { useState } from "react";
import { Crown, Users, ArrowRight } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface FreePlanSeatLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSeats?: number;
  seatLimit?: number;
  attemptedInvites?: number;
}

/**
 * Warning shown when a Free plan workspace tries to invite past its 5-seat limit.
 * Prompts the user to upgrade to a paid plan.
 */
export default function FreePlanSeatLimitDialog({
  open,
  onOpenChange,
  currentSeats = 5,
  seatLimit = 5,
  attemptedInvites = 1,
}: FreePlanSeatLimitDialogProps) {
  const { t } = useTranslation();
  const overBy = Math.max(0, (currentSeats + attemptedInvites) - seatLimit);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Crown className="h-6 w-6 text-amber-500" />
          </div>
          <AlertDialogTitle className="text-center">
            {t("credits.seatLimitTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            <Trans
              i18nKey="credits.seatLimitDescription"
              count={attemptedInvites}
              values={{ seatLimit, overBy }}
              components={{ b: <span className="font-semibold text-foreground" /> }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-2 rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <span>{t("credits.currentSeatsUsed")}</span>
            </div>
            <span className="font-semibold text-foreground">{currentSeats} / {seatLimit}</span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-amber-500" style={{ width: "100%" }} />
          </div>
        </div>

        <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
          <p className="text-xs font-semibold mb-1 flex items-center gap-1.5">
            <Crown className="h-3.5 w-3.5 text-accent" /> {t("credits.upgradeToPaid")}
          </p>
          <p className="text-xs text-muted-foreground">
            <Trans
              i18nKey="credits.seatLimitUpgradeBody"
              components={{ b: <span className="font-medium text-foreground" /> }}
            />
          </p>
        </div>

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel>{t("credits.notNow")}</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button className="gap-1.5">
              {t("credits.upgradePlan")}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Convenience wrapper that renders the dialog with a trigger button (used in the hidden UI menu). */
export function FreePlanSeatLimitDialogPreview() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5">
        <Crown className="h-3.5 w-3.5" />
        {t("credits.seatLimitPreviewButton")}
      </Button>
      <FreePlanSeatLimitDialog
        open={open}
        onOpenChange={setOpen}
        currentSeats={5}
        seatLimit={5}
        attemptedInvites={1}
      />
    </>
  );
}